import { Search, Filter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const DATE_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

interface TeamPostsFilterSidebarProps {
  search: string;
  onSearchChange: (value: string) => void;
  selectedProfile: string;
  onProfileChange: (value: string) => void;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  dateRange: string;
  onDateRangeChange: (value: string) => void;
  profiles: string[];
  categories: string[];
  onClearAll: () => void;
  hasActiveFilters: boolean;
  totalCount: number;
}

export function TeamPostsFilterSidebar({
  search,
  onSearchChange,
  selectedProfile,
  onProfileChange,
  selectedCategory,
  onCategoryChange,
  dateRange,
  onDateRangeChange,
  profiles,
  categories,
  onClearAll,
  hasActiveFilters,
  totalCount,
}: TeamPostsFilterSidebarProps) {
  return (
    <div className="space-y-4 p-4 bg-card rounded-lg border">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Filters</span>
          <Badge variant="secondary">{totalCount.toLocaleString()} posts</Badge>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClearAll} className="h-8">
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search posts..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      <Accordion type="multiple" defaultValue={['profile', 'category', 'date']} className="space-y-2">
        {/* Profile */}
        {profiles.length > 0 && (
          <AccordionItem value="profile" className="border rounded-lg px-3">
            <AccordionTrigger className="text-sm font-medium py-3">Profile</AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {profiles.map((p) => (
                  <div key={p} className="flex items-center space-x-2">
                    <Checkbox
                      id={`profile-${p}`}
                      checked={selectedProfile === p}
                      onCheckedChange={(checked) => onProfileChange(checked ? p : 'all')}
                    />
                    <label htmlFor={`profile-${p}`} className="text-sm leading-none truncate" title={p}>{p}</label>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Category */}
        {categories.length > 0 && (
          <AccordionItem value="category" className="border rounded-lg px-3">
            <AccordionTrigger className="text-sm font-medium py-3">Category</AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="space-y-2">
                {categories.map((c) => (
                  <div key={c} className="flex items-center space-x-2">
                    <Checkbox
                      id={`category-${c}`}
                      checked={selectedCategory === c}
                      onCheckedChange={(checked) => onCategoryChange(checked ? c : 'all')}
                    />
                    <label htmlFor={`category-${c}`} className="text-sm leading-none">{c}</label>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Date Range */}
        <AccordionItem value="date" className="border rounded-lg px-3">
          <AccordionTrigger className="text-sm font-medium py-3">Date Range</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {DATE_OPTIONS.map((d) => (
                <div key={d.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`date-${d.value}`}
                    checked={dateRange === d.value}
                    onCheckedChange={(checked) => onDateRangeChange(checked ? d.value : 'all')}
                  />
                  <label htmlFor={`date-${d.value}`} className="text-sm leading-none">{d.label}</label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
